import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { enumEntityModelCode } from '@app/data-layer/enums/entities';
import { enumEntityType } from '@app/data-layer/enums/isf';
import { AddressModel, ShipmentModel } from '@app/data-layer/models/common';
import { EntityModel } from '@app/data-layer/models/entities';
import { IsfModel } from '@app/data-layer/models/isf';
import { ShipmentsV2Repository } from '@app/data-layer/repositories';
import { FormHelper } from '@helpers/form.helper';
import { UtilsHelper } from '@helpers/utils-helper';
import { ISectionFormField } from '@interfaces/section-form-field';
import { TranslateService } from '@ngx-translate/core';
import { GenericService } from '@services/generic.service';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { IsfOcrQuickFixService, IsfPossibleEntitiesService, IsfStateService } from '@shared/isf-mutual/services';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { IdentifierPipe } from '@shared/pipes-shared/pipes';
import { merge, of, ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-isf-ocr-quick-fix-buyer-details',
  templateUrl: './isf-ocr-quick-fix-buyer-details.component.html',
  styleUrls: ['./isf-ocr-quick-fix-buyer-details.component.scss'],
})
export class IsfOcrQuickFixBuyerDetailsComponent implements OnInit, OnDestroy {
  @ViewChild(MissingFieldsComponent, { static: false }) public missingFieldsModal: MissingFieldsComponent;
  @Input() public buyerFields: ISectionFormField[];

  @Output() public dataSaved: EventEmitter<void> = new EventEmitter();

  public isf: IsfModel;
  public formGroup: UntypedFormGroup;
  public displayErrors: Map<string, string> = new Map<string, string>();
  public formErrors: { [name: string]: IErrorForm };

  public buyerDisplayFields: ISectionFormField[] = [];
  public enumEntityModelCode: typeof enumEntityModelCode = enumEntityModelCode;
  public enumEntityType: typeof enumEntityType = enumEntityType;
  public isEditMode: boolean = false;
  public isDesktop: boolean = false;
  public possibleIsfEntities: EntityModel[] = [];
  public importer: EntityModel;
  public consignee: EntityModel;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private fh: FormHelper,
    private cdr: ChangeDetectorRef,
    private genericService: GenericService,
    private identifierPipe: IdentifierPipe,
    private translateService: TranslateService,
    private isfStateService: IsfStateService,
    private shipmentsV2Repository: ShipmentsV2Repository,
    private isfOcrQuickFixService: IsfOcrQuickFixService,
    private isfPossibleEntitiesService: IsfPossibleEntitiesService,
  ) {}

  ngOnInit(): void {
    const isf = this.isfOcrQuickFixService.getIsf();
    this.syncIsf(isf);

    this.initializeFormGroup();

    this.isEditMode = this.isAnyFormBlockFieldEmpty(this.buyerFields, this.formGroup);

    this.fh.updateEntityValidation(true, this.formGroup);

    this.genericService.isDesktop$.pipe(takeUntil(this.destroy$)).subscribe((isDesktop) => {
      this.isDesktop = isDesktop;
    });

    this.setFieldsProperties();

    this.populateDisplayErrors(this.formGroup);
    this.createFormErrors();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public isAnyFormBlockFieldEmpty(blockFields: ISectionFormField[], fg: UntypedFormGroup): boolean {
    let isFgInvalid: boolean = false;
    Object.keys(fg.controls).forEach((key) => {
      if (fg.controls[key].invalid) {
        isFgInvalid = true;
        return;
      }
    });

    let invalidFields = blockFields.find((field) => fg.get(field.name)?.invalid || !fg.get(field.name)?.value || isFgInvalid);
    return !!invalidFields;
  }

  public onBlockEdit(isShowEditBlock: boolean) {
    this.isEditMode = isShowEditBlock;
    this.formGroup.markAllAsTouched();
    this.formGroup.updateValueAndValidity();
  }

  public onSubmit(isNeedCheck: boolean = true): void {
    if (isNeedCheck && this.formGroup.invalid) {
      const invalidFields = this.fh.validateAllFields(this.formGroup);
      if (Object.keys(invalidFields).length) {
        this.fh.markAsTouched(this.formGroup);
        this.openMissingFieldsModal(invalidFields);
        return;
      }
    }

    this.updateOrCreateBuyer()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isf) => {
        this.isfOcrQuickFixService.setIsf(isf);
        this.isfStateService.updateCurrentIsf(isf);
        this.dataSaved.emit();
      });
  }

  public onClearForm(): void {
    this.formGroup.reset();
  }

  public getEntityOverrideLabel(entity: EntityModel): string {
    return `INFO.ST.USE_${entity.entityCode}_DESKTOP`;
  }

  private openMissingFieldsModal(data) {
    this.missingFieldsModal.openCustomModal(data);
  }

  private setFieldsProperties() {
    const buyer = this.isf?.shipment?.buyer;

    const addressFieldMissingPartsErrorText = this.addressFieldMissingPartsErrorText();
    const buyerAddressDisplayLine = !!addressFieldMissingPartsErrorText ? '' : this.getAddressLines(buyer?.address);
    const buyerIdentifierDisplayLine = this.getIdentifierLine(buyer);
    const nameField = this.buyerFields.find((field) => field.name === 'name');

    this.buyerDisplayFields.push(
      {
        ...nameField,
        type: 'text',
        label: 'LABELS.NAME',
        name: 'name',
        value: buyer?.name,
      },
      {
        type: 'text',
        label: 'LABELS.ADDRESS',
        name: 'address',
        value: buyerAddressDisplayLine,
      },
      {
        type: 'text',
        label: 'LABELS.ID_NUMBER',
        name: 'identifier',
        value: buyerIdentifierDisplayLine,
      },
    );

    this.cdr.markForCheck();
  }

  private getIdentifierLine(entity: EntityModel): string {
    if (!entity?.entityIdentifierQualifier && !entity?.entityIdentifier) {
      return '';
    }

    const identifierQualifierText = this.translateService.instant(`IDENTIFIER_QUALIFIER.${entity.entityIdentifierQualifier}`);
    const identifierText = this.identifierPipe.transform(entity.entityIdentifier);
    return `${identifierQualifierText} ${identifierText}`;
  }

  private populateDisplayErrors(fg: UntypedFormGroup) {
    Object.keys(fg.controls).forEach((fieldName) => {
      switch (fieldName) {
        case 'name':
          this.displayErrors.set(fieldName, this.fieldMissingErrorText(fieldName, fg));
          break;
        case 'address':
          this.displayErrors.set(fieldName, this.addressFieldMissingPartsErrorText());
          break;
        default:
          break;
      }
    });
  }

  private createFormErrors(): void {
    this.formErrors = this.fh.createErrorsForIsfFormGroup();
  }

  private fieldMissingErrorText(fieldName: string | string[], fg: UntypedFormGroup): string {
    return !fg.get(fieldName)?.value ? 'LABELS.MISSING' : '';
  }

  private addressFieldMissingPartsErrorText(): string {
    const buyerAddressErrors = this.isf?.shipment?.buyer?.address?.validationMessages || {};
    const missingAddressPartsText = !UtilsHelper.isObjectEmpty(buyerAddressErrors) ? 'BACK_ERROR.MISSING_ADDRESS_PARTS' : '';
    return missingAddressPartsText;
  }

  private getAddressLines(data: AddressModel): string {
    if (!data) {
      return '';
    }

    const state = data.state ? data.state.description : null;
    const country = data.country ? data.country.description : null;
    const address = [data?.line1, data?.line2, data?.line3]
      .filter((part) => !!part)
      .join('\n')
      .trim();

    const line1 = [address, data.city]
      .filter((part) => !!part)
      .join(', ')
      .trim();
    const line2 = [country, state]
      .filter((part) => !!part)
      .join(', ')
      .trim();

    return line1 + line2;
  }

  private updateOrCreateBuyer() {
    const buyer: EntityModel = this.formGroup.value;
    const buyerId = this.formGroup.get('id').value;

    let newShipmentData: Partial<ShipmentModel>;

    if (buyerId === this.importer?.id) {
      newShipmentData = {
        ...this.isf.shipment,
        buyer: this.importer,
        buyerId: this.importer?.id,
      };
    } else if (buyerId === this.consignee?.id) {
      newShipmentData = {
        ...this.isf.shipment,
        buyer: this.consignee,
        buyerId: this.consignee?.id,
      };
    } else {
      newShipmentData = {
        ...this.isf.shipment,
        buyer,
        buyerId,
      };
    }

    return of(buyer).pipe(
      switchMap((entity) => {
        if (!buyerId) {
          entity.addressId = 0;
          entity.address.id = 0;
          entity.entityCode = enumEntityModelCode.BUYER;
          return this.isfStateService.createEntity(entity);
        } else {
          return this.isfStateService.updateEntity(entity);
        }
      }),
      switchMap((entity) => {
        return this.updateShipment({
          ...newShipmentData,
          buyer: entity,
          buyerId: entity.id,
        });
      }),
      switchMap((newShipment) => this.isfStateService.updateShipment(newShipment)),
      switchMap((shipment) => {
        return this.isfStateService.updateIsf({
          ...this.isf,
          shipment,
        });
      }),
      switchMap((isf) => this.isfStateService.loadIsf(isf.referenceNumber)),
      tap((isf) => {
        this.syncIsf(isf);
      }),
    );
  }

  private updateShipment(newShipmentData?: Partial<ShipmentModel>) {
    if (!newShipmentData) {
      newShipmentData = { ...this.isf?.shipment };
    }

    return this.shipmentsV2Repository.update(this.isf?.shipment?.id, newShipmentData);
  }

  private initializeFormGroup() {
    this.formGroup = this.fh.createEntityModelFormGroup(enumEntityModelCode.BUYER);

    this.formGroup.patchValue(this.isf?.shipment?.buyer);

    this.formGroup
      .get('id')
      .valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged(), startWith(this.formGroup.get('id').value))
      .subscribe((buyerId) => {
        this.applyUsedEntityOrBuyer(buyerId);
      });

    merge(
      this.formGroup.get('entityIdentifier').valueChanges,
      this.formGroup.get('entityIdentifierQualifier').valueChanges,
      this.formGroup.get('name').valueChanges,
      this.formGroup.get('address').valueChanges,
    )
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((_) => {
        const isUsedEntityResetNeeded =
          !this.formGroup.pristine &&
          ((this.importer && this.formGroup.get('id').value === this.importer.id) ||
            (this.consignee && this.formGroup.get('id').value === this.consignee.id));

        if (isUsedEntityResetNeeded) {
          this.formGroup.get('id').patchValue(this.isf?.shipment?.buyer?.id || 0);
        }
      });

    if (this.formGroup.invalid) {
      this.formGroup.markAsDirty();
      this.formGroup.markAllAsTouched();
    }
  }

  private applyUsedEntityOrBuyer(id: number) {
    if (this.importer && id === this.importer.id) {
      this.formGroup.markAsPristine();
      this.fh.patchIsfEntity(this.formGroup, this.importer);
    } else if (this.consignee && id === this.consignee.id) {
      this.formGroup.markAsPristine();
      this.fh.patchIsfEntity(this.formGroup, this.consignee);
    } else {
      this.fh.patchIsfEntity(this.formGroup, this.isf?.shipment?.buyer);
    }
  }

  private syncIsf(isf: IsfModel) {
    this.isf = isf;
    this.importer = this.isf?.importer;
    this.consignee = this.isf?.shipment?.consignee;
    this.possibleIsfEntities = this.isfPossibleEntitiesService.calculatePossibleIsfEntities(
      enumEntityModelCode.BUYER,
      this.possibleIsfEntities,
    );
  }
}
